import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { SimpleButton, ErrorMessage } from '@/src/ui';
import {
    MatrixRenderer,
    ControlledDynamicTextEditor,
    isEditorContentEmpty,
    type QuestionMetaDTO,
    type QtiMatrixQuestionDTO,
    type MatrixCell,
} from '@/src/lib';
import {
    Debugger,
    QuestionMetaForm,
    TextEntryModal,
    QuestionTitleField,
    type TextEntryModalPartialProps,
} from '../_shared';
import { defaultMeta } from '../../models/questionDTOs';
import { metaSchema } from '../../models/schema';
import TableEditor from './components/TableEditor';
import { renderLoaders } from '../../utils';
import { saveQuestion } from '../../services';
import { Constants } from '@/src/utils';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const schema = yup.object().shape({
    id: yup.number().optional(),
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .max(
            Constants.MAX_CHARS.TITLE,
            `Title cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    prompt: yup
        .string()
        .test(
            'not-empty',
            'Prompt cannot be empty.',
            (value) => !isEditorContentEmpty(value || '')
        ),
    prompt_es: yup.string().when('$isSpanishAllowed', {
        is: true,
        then: (schema) =>
            schema
                .test(
                    'not-empty',
                    'Prompt (Spanish) cannot be empty.',
                    (value) => !isEditorContentEmpty(value || '')
                )
                .required('Prompt (Spanish) is required'),
        otherwise: (schema) => schema.optional(),
    }),
    additionalMeta: yup.object({
        excludeHeaderRow: yup.boolean().required(),
        excludeHeaderColumn: yup.boolean().required(),
    }),
    rows: yup
        .array()
        .of(
            yup.object({
                id: yup.string().required(),
                text: yup.string().when(['$excludeHeaderColumn'], {
                    is: (excludeHeaderColumn: boolean) => !excludeHeaderColumn,
                    then: (schema) =>
                        schema
                            .test(
                                'not-empty',
                                'Row label cannot be empty.',
                                (value) => !isEditorContentEmpty(value || '')
                            )
                            .required('Row label is required'),
                    otherwise: (schema) => schema.optional(),
                }),
                text_es: yup.string().when(['$isSpanishAllowed', '$excludeHeaderColumn'], {
                    is: (isSpanishAllowed: boolean, excludeHeaderColumn: boolean) =>
                        isSpanishAllowed && !excludeHeaderColumn,
                    then: (schema) =>
                        schema
                            .test(
                                'not-empty',
                                'Row label (Spanish) cannot be empty.',
                                (value) => !isEditorContentEmpty(value || '')
                            )
                            .required('Row label (Spanish) is required'),
                    otherwise: (schema) => schema.optional(),
                }),
            })
        )
        .min(1, 'At least one row is required'),
    columns: yup
        .array()
        .of(
            yup.object({
                id: yup.string().required(),
                text: yup.string().when(['$excludeHeaderRow'], {
                    is: (excludeHeaderRow: boolean) => !excludeHeaderRow,
                    then: (schema) =>
                        schema
                            .test(
                                'not-empty',
                                'Column label cannot be empty.',
                                (value) => !isEditorContentEmpty(value || '')
                            )
                            .required('Column label is required'),
                    otherwise: (schema) => schema.optional(),
                }),
                text_es: yup.string().when(['$isSpanishAllowed', '$excludeHeaderRow'], {
                    is: (isSpanishAllowed: boolean, excludeHeaderRow: boolean) =>
                        isSpanishAllowed && !excludeHeaderRow,
                    then: (schema) =>
                        schema
                            .test(
                                'not-empty',
                                'Column label (Spanish) cannot be empty.',
                                (value) => !isEditorContentEmpty(value || '')
                            )
                            .required('Column label (Spanish) is required'),
                    otherwise: (schema) => schema.optional(),
                }),
            })
        )
        .min(1, 'At least one column is required'),
    cells: yup.array().of(
        yup.object({
            rowId: yup.string().required(),
            columnId: yup.string().required(),
            type: yup.string().oneOf(['radio', 'check', 'openText', 'staticText']).required(),
            isCorrect: yup
                .boolean()
                .nullable()
                .when('type', {
                    is: (val: string) => val === 'radio' || val === 'check',
                    then: (schema) => schema.required('Correctness is required for choice cells'),
                    otherwise: (schema) => schema.optional(),
                }),
            staticContent: yup.string().when('type', {
                is: 'staticText',
                then: (schema) =>
                    schema
                        .test(
                            'not-empty',
                            'Static content is required.',
                            (value) => !isEditorContentEmpty(value || '')
                        )
                        .required('Static content is required'),
                otherwise: (schema) => schema.optional(),
            }),
            staticContent_es: yup.string().when(['type', '$isSpanishAllowed'], {
                is: (type: string, isSpanishAllowed: boolean) =>
                    type === 'staticText' && isSpanishAllowed,
                then: (schema) =>
                    schema
                        .test(
                            'not-empty',
                            'Static content (Spanish) is required.',
                            (value) => !isEditorContentEmpty(value || '')
                        )
                        .required('Static content (Spanish) is required'),
                otherwise: (schema) => schema.optional(),
            }),
            expectedText: yup.string().when('type', {
                is: 'openText',
                then: (schema) =>
                    schema
                        .test(
                            'not-empty',
                            'Expected answer is required.',
                            (value) => !isEditorContentEmpty(value || '')
                        )
                        .required('Expected answer is required'),
                otherwise: (schema) => schema.optional(),
            }),
            expectedText_es: yup.string().when(['type', '$isSpanishAllowed'], {
                is: (type: string, isSpanishAllowed: boolean) =>
                    type === 'openText' && isSpanishAllowed,
                then: (schema) =>
                    schema
                        .test(
                            'not-empty',
                            'Expected answer (Spanish) is required.',
                            (value) => !isEditorContentEmpty(value || '')
                        )
                        .required('Expected answer (Spanish) is required'),
                otherwise: (schema) => schema.optional(),
            }),
        })
    ),
    meta: metaSchema.required(),
});

interface MatrixEditorProps {
    initialModel?: QtiMatrixQuestionDTO;
}

export default function MatrixEditor({ initialModel }: MatrixEditorProps) {
    const isEdit = !!initialModel;
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleError, setIsTitleError] = useState(false);
    const [modalProps, setModalProps] = useState<TextEntryModalPartialProps | null>(null);

    const defaultValues: QtiMatrixQuestionDTO = initialModel
        ? initialModel
        : {
              title: '',
              prompt: '',
              prompt_es: '',
              rows: [],
              columns: [],
              cells: [],
              additionalMeta: {
                  excludeHeaderRow: false,
                  excludeHeaderColumn: false,
              },
              meta: { ...defaultMeta, questionTypeMasterId: qtm.MATRIX },
          };

    const [formContext, setFormContext] = useState({
        excludeHeaderRow: defaultValues.additionalMeta.excludeHeaderRow,
        excludeHeaderColumn: defaultValues.additionalMeta.excludeHeaderColumn,
        isSpanishAllowed: defaultValues.meta.isSpanishAllowed,
    });

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
        setError: setFormError,
        clearErrors,
    } = useForm<QtiMatrixQuestionDTO>({
        defaultValues,
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
        context: formContext,
    });

    const question = watch();
    useEffect(() => {
        setFormContext({
            excludeHeaderRow: question.additionalMeta.excludeHeaderRow,
            excludeHeaderColumn: question.additionalMeta.excludeHeaderColumn,
            isSpanishAllowed: question.meta.isSpanishAllowed,
        });
    }, [
        question.additionalMeta.excludeHeaderRow,
        question.additionalMeta.excludeHeaderColumn,
        question.meta.isSpanishAllowed,
    ]);

    const handleLanguageSet = useCallback(
        (language: 'en' | 'es') => {
            if (language === 'en') return;

            if (!question.prompt_es) {
                setValue('prompt_es', question.prompt);
            }
            question.rows.forEach((row, index) => {
                if (!row.text_es) {
                    setValue(`rows.${index}.text_es`, row.text);
                }
            });
            question.cells.forEach((cell, index) => {
                if (cell.type === 'staticText' && !cell.staticContent_es) {
                    setValue(`cells.${index}.staticContent_es`, cell.staticContent || '');
                }
                if (cell.type === 'openText' && !cell.expectedText_es) {
                    setValue(`cells.${index}.expectedText_es`, cell.expectedText || '');
                }
            });
        },
        [question, setValue]
    );

    const onSubmit = async () => {
        if (success) return;
        if (isTitleError) {
            setError('Please choose a unique title.');
            return;
        }

        clearErrors();
        setLoading(true);
        setError(null);
        try {
            const viewMode = isEdit ? 'edit' : 'create';
            await saveQuestion(question, viewMode);
            setSuccess(true);
        } catch (e: any) {
            setError(e.message || 'Unknown error');
            setFormError('root', { type: 'manual', message: e.message || 'Unknown error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            key={`MatrixEditor-${question.meta.isSpanishAllowed}-${question.additionalMeta.excludeHeaderRow}-${question.additionalMeta.excludeHeaderColumn}`}
            className="p-4 space-y-4"
            onSubmit={handleSubmit(onSubmit)}
        >
            {/* Handles all Spanish entry */}
            {modalProps && (
                <TextEntryModal
                    initialData={modalProps.initialData}
                    fieldName={modalProps.fieldName}
                    friendlyName={modalProps.friendlyName}
                    textMode={modalProps.textMode}
                    error={modalProps.error}
                    control={control}
                    handleClose={() => setModalProps(null)}
                />
            )}
            {renderLoaders(loading, success, error, isEdit)}

            <h2>Matrix Question Title:</h2>
            <QuestionTitleField
                id={question.id}
                title={question.title}
                control={control}
                setIsTitleError={setIsTitleError}
                error={errors.title}
            />

            <div className="my-4" />

            <div className="flex flex-row gap-4">
                <h2>Prompt:</h2>
                {question.meta.isSpanishAllowed && (
                    <div
                        onClick={() =>
                            setModalProps({
                                initialData: question.prompt_es,
                                fieldName: 'prompt_es',
                                friendlyName: 'Prompt (Spanish)',
                                textMode: 'rich',
                                error: errors.prompt_es?.message,
                            })
                        }
                        style={{ cursor: 'pointer' }}
                    >
                        Edit Spanish
                    </div>
                )}
                <ErrorMessage error={errors.prompt_es} />
            </div>

            <ControlledDynamicTextEditor
                name="prompt"
                control={control}
                initialValue={question.prompt}
                style={{ minHeight: '150px' }}
            />
            <ErrorMessage error={errors.prompt} />

            <div className="my-4" />

            <TableEditor
                question={question}
                setModalProps={setModalProps}
                onCellsChange={(cells: MatrixCell[]) => setValue('cells', cells)}
                control={control}
                errors={errors}
            />

            <div className="my-4" />

            <QuestionMetaForm
                rhfProps={{ control, errors: errors.meta, watch }}
                handleLanguageSet={handleLanguageSet}
                disableList={['shouldShuffleOptions']}
                metaValues={watch('meta')}
                setMetaValues={(meta: QuestionMetaDTO) => setValue('meta', meta)}
            />

            <div className="border rounded p-4 bg-white max-w-4xl space-y-4 mt-4">
                <h4 className="font-semibold mb-1">Interactive Preview</h4>
                <MatrixRenderer
                    question={question}
                    options={{
                        showLanguageToggle: question.meta.isSpanishAllowed,
                        showScoring: true,
                    }}
                />
            </div>

            <div className="flex items-center space-x-2">
                <div>
                    <SimpleButton
                        type="submit"
                        color="primary"
                        className="bg-green-600 text-white px-3 py-1 rounded text-xs"
                        disabled={isSubmitting || loading}
                    >
                        {isEdit ? 'Update Question' : 'Create Question'}
                    </SimpleButton>
                </div>
            </div>
            <ErrorMessage
                error={Object.keys(errors).length ? 'Please correct the errors to continue' : ''}
            />
            {renderLoaders(loading, success, error, isEdit)}

            <Debugger question={question} errors={errors} />
        </form>
    );
}
